import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { normalizeTag, typeTagLabel, type TypeTagCount } from "../lib/type-tags";

/** 줄에 펼쳐 보이는 칩 수 (All 제외). 나머지는 "+ N more" 안으로 */
const VISIBLE_LIMIT = 7;
const MOBILE_QUERY = "(max-width: 749px)";

type Props = {
  counts: TypeTagCount[];
  /** 범위 안 상품 수 (All 칩 숫자) */
  total: number;
  loading: boolean;
  selected: string[];
  onChange: (tags: string[]) => void;
};

type MenuPos = {
  left: number;
  top: number;
  maxHeight: number;
  /** 아래 공간이 모자라 버튼 위로 띄울 때: 메뉴의 아래 변이 닿아야 할 y */
  anchorBottom?: number;
};

/** 데스크톱 팝오버 폭 · 아래 여백(테마의 Rewards 같은 플로팅 버튼이 덮지 않게) */
const MENU_WIDTH = 320;
const MENU_MAX_HEIGHT = 520;
const MENU_MIN_HEIGHT = 300;
const VIEWPORT_BOTTOM_RESERVE = 110;

function isMobile(): boolean {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * 종류(TYPE) 칩 줄 — 툴바 아래, 그룹 줄처럼 가로 스크롤.
 * 칩은 바로 토글(다중, OR). "+ N more" 는 데스크톱 팝오버 / 모바일 바텀시트 (body 포털).
 */
export function TypeTagBar({ counts, total, loading, selected, onChange }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<string[]>(selected);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedKeys = useMemo(
    () => new Set(selected.map(normalizeTag)),
    [selected]
  );
  const isSelected = (tag: string) => selectedKeys.has(normalizeTag(tag));

  // 줄에 보이는 칩: 선택된 것 먼저(범위에 없어도 — URL 로 들어온 경우), 그다음 건수 순
  const { visible, hidden } = useMemo(() => {
    const byKey = new Map(counts.map((c) => [normalizeTag(c.tag), c]));
    const pinned: TypeTagCount[] = selected.map(
      (t) => byKey.get(normalizeTag(t)) ?? { tag: t, count: 0 }
    );
    const rest = counts.filter((c) => !selectedKeys.has(normalizeTag(c.tag)));
    const room = Math.max(VISIBLE_LIMIT - pinned.length, 0);
    return { visible: [...pinned, ...rest.slice(0, room)], hidden: rest.slice(room) };
  }, [counts, selected, selectedKeys]);

  const toggle = (tag: string) => {
    const key = normalizeTag(tag);
    onChange(
      selectedKeys.has(key)
        ? selected.filter((t) => normalizeTag(t) !== key)
        : [...selected, tag]
    );
  };

  const openMenu = () => {
    setDraft(selected);
    setQuery("");
    const btn = moreBtnRef.current;
    if (btn && !isMobile()) {
      const r = btn.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - MENU_WIDTH - 8));
      let top = r.bottom + 8;
      let below = window.innerHeight - top - VIEWPORT_BOTTOM_RESERVE;
      const above = r.top - 8 - 8;
      // 아래 공간이 모자라면 페이지를 그만큼 내려서(가능한 만큼) 버튼 아래에 연다
      if (below < MENU_MIN_HEIGHT) {
        const room = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight - window.scrollY
        );
        const d = Math.min(MENU_MIN_HEIGHT - below, room);
        if (d > 0) {
          window.scrollBy(0, d);
          top -= d;
          below += d;
        }
      }
      if (below >= MENU_MIN_HEIGHT || below >= above) {
        setMenuPos({ left, top, maxHeight: Math.max(MENU_MIN_HEIGHT, Math.min(MENU_MAX_HEIGHT, below)) });
      } else {
        // 아래가 좁으면 버튼 위로 — 높이를 알아야 top 이 나오므로 layout effect 에서 맞춘다
        setMenuPos({ left, top: 8, maxHeight: Math.max(240, Math.min(MENU_MAX_HEIGHT, above)), anchorBottom: r.top - 8 });
      }
    } else {
      setMenuPos(null);
    }
    setMenuOpen(true);
  };
  const closeMenu = () => setMenuOpen(false);

  // 버튼 위로 띄우는 경우: 실제 높이를 재서 아래 변을 버튼 위에 맞춘다
  useLayoutEffect(() => {
    if (!menuOpen || !menuPos?.anchorBottom || !menuRef.current) return;
    const h = menuRef.current.offsetHeight;
    const top = Math.max(8, menuPos.anchorBottom - h);
    if (top !== menuPos.top) setMenuPos((p) => (p ? { ...p, top } : p));
  }, [menuOpen, menuPos]);

  // Esc 닫기 · 모바일 시트일 때 바디 스크롤 잠금 (언마운트 시 반드시 원복)
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const lock = menuPos == null;
    const prevOverflow = document.body.style.overflow;
    if (lock) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      if (lock) document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen, menuPos]);

  // 화면 크기가 바뀌면(회전 등) 팝오버 좌표가 틀어지므로 닫는다
  useEffect(() => {
    if (!menuOpen) return;
    const onResize = () => setMenuOpen(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [menuOpen]);

  // 선택이 바뀌면 고른 칩이 맨 앞에 오므로 줄을 처음으로 되돌린다
  // ("+ N more" 버튼에 포커스가 가면서 줄이 옆으로 밀려 있을 수 있음)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  }, [selected]);

  const draftKeys = useMemo(() => new Set(draft.map(normalizeTag)), [draft]);
  const toggleDraft = (tag: string) => {
    const key = normalizeTag(tag);
    setDraft((d) =>
      draftKeys.has(key) ? d.filter((t) => normalizeTag(t) !== key) : [...d, tag]
    );
  };
  const applyDraft = () => {
    onChange(draft);
    setMenuOpen(false);
  };
  const clearAll = () => {
    onChange([]);
    setMenuOpen(false);
  };

  const q = query.trim().toLowerCase();
  const menuItems = q
    ? counts.filter((c) => c.tag.toLowerCase().includes(q))
    : counts;

  // 보여줄 게 하나도 없으면 줄 자체를 그리지 않는다 (사전 로드 실패 포함)
  if (!loading && counts.length === 0 && selected.length === 0) return null;

  const hasSelection = selected.length > 0;

  return (
    <div className="poca-typebar" aria-busy={loading}>
      <div className="poca-typebar__row">
        <span className="poca-typebar__label">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M3 5h14M5.5 10h9M8.5 15h5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          Type
          {loading && counts.length > 0 && (
            <span className="poca-typebar__spin" aria-label="Counting" title="Counting…" />
          )}
        </span>

        <div
          ref={scrollRef}
          className="poca-typebar__scroll"
          role="group"
          aria-label="Filter by type"
        >
          <button
            type="button"
            className={`poca-typechip${!hasSelection ? " poca-typechip--on" : ""}`}
            onClick={() => hasSelection && onChange([])}
            aria-pressed={!hasSelection}
          >
            {!hasSelection && <span className="poca-typechip__check" aria-hidden="true">✓</span>}
            All
            {total > 0 && <span className="poca-typechip__count">{total}</span>}
          </button>

          {visible.map((c) => {
            const on = isSelected(c.tag);
            return (
              <button
                key={normalizeTag(c.tag)}
                type="button"
                className={`poca-typechip${on ? " poca-typechip--on" : ""}`}
                onClick={() => toggle(c.tag)}
                aria-pressed={on}
              >
                {on && <span className="poca-typechip__check" aria-hidden="true">✓</span>}
                {typeTagLabel(c.tag)}
                {c.count > 0 && <span className="poca-typechip__count">{c.count}</span>}
              </button>
            );
          })}

          {loading && counts.length === 0 && (
            <span className="poca-typechip poca-typechip--ghost" aria-hidden="true">
              Loading…
            </span>
          )}

          {hidden.length > 0 && (
            <button
              ref={moreBtnRef}
              type="button"
              className="poca-typechip poca-typechip--more"
              onClick={openMenu}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
            >
              + {hidden.length} more
            </button>
          )}
        </div>

        {hasSelection && (
          <button type="button" className="poca-typebar__clear" onClick={clearAll}>
            Clear
          </button>
        )}
      </div>

      {menuOpen &&
        createPortal(
          <>
            <button
              type="button"
              className="poca-typemenu__backdrop"
              onClick={closeMenu}
              aria-label="Close"
              tabIndex={-1}
            />
            <div
              ref={menuRef}
              className={`poca-typemenu${menuPos ? "" : " poca-typemenu--sheet"}`}
              style={
                menuPos
                  ? { left: menuPos.left, top: menuPos.top, maxHeight: menuPos.maxHeight }
                  : undefined
              }
              role="dialog"
              aria-modal="true"
              aria-label="Filter by type"
            >
              {/* 적용/해제는 위쪽에 — 아래쪽은 테마의 플로팅 버튼(Rewards·채팅)이 덮을 수 있다 */}
              <div className="poca-typemenu__head">
                <button
                  type="button"
                  className="poca-typemenu__btn poca-typemenu__btn--ghost"
                  onClick={clearAll}
                  disabled={draft.length === 0 && selected.length === 0}
                >
                  Clear
                </button>
                <span className="poca-typemenu__title">Type</span>
                <button
                  type="button"
                  className="poca-typemenu__btn"
                  onClick={applyDraft}
                >
                  Apply{draft.length > 0 ? ` (${draft.length})` : ""}
                </button>
              </div>
              <div className="poca-typemenu__search">
                <input
                  type="search"
                  className="poca-typemenu__input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search type"
                  aria-label="Search type"
                  autoFocus={menuPos != null}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyDraft();
                  }}
                />
              </div>
              <div className="poca-typemenu__list">
                {menuItems.length === 0 ? (
                  <p className="poca-typemenu__empty">No matching type.</p>
                ) : (
                  menuItems.map((c) => {
                    const on = draftKeys.has(normalizeTag(c.tag));
                    return (
                      <button
                        key={normalizeTag(c.tag)}
                        type="button"
                        className={`poca-typemenu__item${on ? " poca-typemenu__item--on" : ""}`}
                        onClick={() => toggleDraft(c.tag)}
                        aria-pressed={on}
                      >
                        <span className="poca-typemenu__name">{typeTagLabel(c.tag)}</span>
                        <span className="poca-typemenu__count">{c.count}</span>
                        <span className="poca-typemenu__box" aria-hidden="true">
                          {on ? "✓" : ""}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
              <div className="poca-typemenu__foot">
                <span className="poca-typemenu__note">
                  {counts.length} types · counts for current group{loading ? " · counting…" : ""}
                </span>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
